import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import '../DailyAttendace/UserSelfAttendance/MyAttendance.css'
import { useNavigate } from 'react-router-dom';
import Select from "react-select";
import * as tf from "@tensorflow/tfjs";
import * as faceDetection from "@tensorflow-models/face-detection";
import "@mediapipe/face_detection";
import { selfAttendance } from '../../services/monthlyAttendanceService';


const CameraAndLocation = () => {
    // ... [EXISTING STATE AND UTILITY FUNCTIONS REMAINS UNCHANGED] ...

    console.debug("🔄 Component Render");
    const employeeName = localStorage.getItem("full_name") || "N/A";
    const employeeId = localStorage.getItem('employeeId') || "N/A";
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [selectedGodownId, setSelectedGodownId] = useState(null);
    const [successPopup, setSuccessPopup] = useState(false);

    const [location, setLocation] = useState({ latitude: null, longitude: null });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    const [cameraActive, setCameraActive] = useState(false);
    const [capturedPhoto, setCapturedPhoto] = useState(null);
    const [stream, setStream] = useState(null);

    const [faceErrorPopup, setFaceErrorPopup] = useState(false);
    const [locationPopup, setLocationPopup] = useState(false);
    const [distanceFromGodown, setDistanceFromGodown] = useState(0);

    const gd = localStorage.getItem('gd');
    const godowns = gd ? JSON.parse(gd) : [];

    useEffect(() => {
        if (godowns.length === 1) {
            setSelectedGodownId(godowns[0].godownId);
        }
    }, [godowns]);

    let selectedGodownData = godowns.find(g => g.godownId === selectedGodownId);

    // Access Restriction (KEEPING THIS LOGIC)
    // useEffect(() => {
    //     const allowedIds = ["4G-20251016935475", "4G-20251016894060", "4G-20251016455281"];
    //     if (!allowedIds.includes(employeeId)) {
    //         alert("Access restricted to authorized personnel only.");
    //         navigate('/');
    //     }
    // }, [employeeId, navigate]);

    const today = new Date();
    const currentDate = format(today, 'dd-MM-yyyy HH:mm');

    // FACE DETECTION UTILITIES (UNMODIFIED)
    let model = useRef(null);

    const loadFaceModel = async () => {
        if (!model.current) {
            model.current = await faceDetection.createDetector(
                faceDetection.SupportedModels.MediaPipeFaceDetector,
                { runtime: "tfjs", maxFaces: 1 }
            );
            console.log("🤖 Face Detection Model Loaded");
        }
    };

    const detectFace = async (imageDataUrl) => {
        await loadFaceModel();
        return new Promise((resolve) => {
            const img = new Image();
            img.src = imageDataUrl;
            img.onload = async () => {
                const faces = await model.current.estimateFaces(img);
                resolve(faces.length > 0);
            };
        });
    };

    // -------------------------
    // CAMERA HANDLING
    // -------------------------
    const cameraStarted = useRef(false);

    const startCamera = async () => {
        console.log("📸 startCamera() called");
        cameraStarted.current = true; // Use current ref flag

        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
                audio: false
            });

            setStream(mediaStream);
            setCameraActive(true);

        } catch (err) {
            console.error("❌ Camera Error:", err);
            setSubmitMessage("Camera access blocked! Allow camera permission.");
            setMessageType("error");
            setCameraActive(false);
            cameraStarted.current = false;
        }
    };

    useEffect(() => {
        if (cameraActive && stream && videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => {
                videoRef.current.play().catch(err => console.error("Play error:", err));
            };
        } else if (!cameraActive && videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, [stream, cameraActive]);


    const capturePhoto = async () => {
        // ... [EXACT SAME capturePhoto LOGIC] ...
        const video = videoRef.current;
        const canvas = canvasRef.current;

        if (!video || !canvas) {
            console.error("❌ capturePhoto FAILED → video/canvas missing");
            return;
        }

        if (video.readyState < 2) {
            await new Promise(resolve => video.onloadeddata = resolve);
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const photoDataUrl = canvas.toDataURL("image/jpeg", 0.9);

        // FACE DETECTION
        const hasFace = await detectFace(photoDataUrl);

        if (!hasFace) {
            setFaceErrorPopup(true);
            setCapturedPhoto(null);
            // Optionally, stop and restart stream to reset state cleanly, or rely on popup to trigger retake
            stream.getTracks().forEach(track => track.stop());
            setCameraActive(false);
            return;
        }

        // FACE FOUND → SAVE PHOTO
        setCapturedPhoto(photoDataUrl);

        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
        setCameraActive(false);
    };


    // Cleanup Camera on unmount (UNMODIFIED)
    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [stream]);

    // Retake (UNMODIFIED)
    const retakePhoto = () => {
        setCapturedPhoto(null);
        startCamera();
    };

    // -------------------------
    // LOCATION HANDLING (MODIFIED TO BE CALLED MANUALLY)
    // -------------------------

    const fetchLocation = () => {
        console.debug("📍 fetchLocation() called");

        if (navigator.geolocation) {
            setSubmitMessage('Fetching location...');
            setMessageType('');
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    console.debug("✅ Location Obtained:", pos.coords);
                    setLocation({
                        latitude: parseFloat(pos.coords.latitude.toFixed(6)),
                        longitude: parseFloat(pos.coords.longitude.toFixed(6)),
                    });
                    setSubmitMessage('Location obtained.');
                    setMessageType('success');
                },
                (err) => {
                    console.error("❌ Location Error:", err);
                    setSubmitMessage(`Location Error: ${err.message}. Allow location permission.`);
                    setMessageType('error');
                    setLocation({ latitude: null, longitude: null }); // Reset on error
                }
            );
        } else {
            console.error("❌ Geolocation Not Supported");
            setSubmitMessage("Geolocation Not Supported by this device/browser.");
            setMessageType('error');
        }
    }


    // -------------------------
    // SUBMIT ATTENDANCE (UNMODIFIED)
    // -------------------------
    function calculateDistance(lat1, lon1, lat2, lon2) {
        const R = 6371e3; // meters
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ / 2) ** 2 +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) ** 2;

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        const meters = R * c;
        return meters / 1000;   // returns KM with decimal
    }

    function base64ToFile(base64, filename) {
        // ... [EXACT SAME base64ToFile LOGIC] ...
        const arr = base64.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    }

    const handleSubmit = async () => {
        // ... [EXACT SAME handleSubmit LOGIC] ...
        if (!selectedGodownId) {
            setSubmitMessage('Please select your working location!');
            setMessageType('error');
            return;
        }

        if (!location.latitude) {
            setSubmitMessage('Location not available. Please fetch location.');
            setMessageType('error');
            return;
        }

        if (!capturedPhoto) {
            setSubmitMessage('Please take your selfie');
            setMessageType('error');
            return;
        }

        // FACE CHECK AGAIN
        const hasFace = await detectFace(capturedPhoto);
        if (!hasFace) {
            setSubmitMessage("Face not detected — please retake your photo.");
            setMessageType("error");
            setCapturedPhoto(null);
            startCamera();
            return;
        }

        selectedGodownData = godowns.find(g => g.godownId === selectedGodownId);

        const dist = calculateDistance(
            location.latitude,
            location.longitude,
            selectedGodownData.latitude,
            selectedGodownData.longitude
        );

        setDistanceFromGodown(dist);

        if (dist > 150) {
            setLocationPopup(true);
            return;
        }

        submitAttendance();
    };

    const submitAttendance = async () => {
        // ... [EXACT SAME submitAttendance LOGIC] ...
        setIsSubmitting(true);
        setSubmitMessage('');
        setMessageType('');

        const file = base64ToFile(capturedPhoto, "selfie.jpg");

        let messageValue = null;
        if (distanceFromGodown > 0.2) {
            messageValue = `You are ${distanceFromGodown.toFixed(3)} km away from the location`;
        }

        const formData = new FormData();
        formData.append("employeeId", employeeId);
        formData.append("godownId", selectedGodownId);
        formData.append("latitude", location.latitude);
        formData.append("longitude", location.longitude);
        formData.append("message", messageValue);
        formData.append("selfieFile", file);

        try {
            const response = await selfAttendance(formData);
            const result = response.data;
            let messageText = (typeof result === "object" && result.message) ? result.message : (typeof result === "string" ? result : "Unknown response");

            if (messageText === "Attendance captured successfully!") {
                setSubmitMessage("");
                setMessageType("success");
                setSuccessPopup(true);
            } else if (messageText === "Attendance already captured for today!") {
                setSubmitMessage(messageText);
                setMessageType("duplicate");
            } else {
                setSubmitMessage(messageText || "Failed to mark attendance");
                setMessageType("error");
            }
        } catch (err) {
            let errorMessage = err.response?.data?.message || err.response?.data || "Network error";
            if (typeof errorMessage !== 'string') errorMessage = "Network error";

            if (errorMessage.toLowerCase().includes("already") || errorMessage.toLowerCase().includes("captured")) {
                setSubmitMessage(errorMessage);
                setMessageType("duplicate");
                setSuccessPopup(true);
            } else {
                setSubmitMessage(errorMessage || "Network error");
                setMessageType("error");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const godownOptions = godowns.map(g => ({
        value: g.godownId,
        label: `${g.godownName} (${g.district})`
    }));

    // -------------------------
    // RENDER SECTION (MODIFIED)
    // -------------------------

    return (
        <div className="page-container">
            <h3 className="page-title">Mark Attendance</h3>

            <div className="attendance-card">
                <div className="card-body">

                    {/* INFO BLOCK */}
                    <div className="info-grid">
                        <div className=" text-nowrap label">Date & Time :</div><div className=" text-end valuea"> {currentDate}</div>
                        <div className="label">Work Location :</div>
                        <div className={`valuea ${godowns.length === 1 ? "single-godown" : "multi-godown"}`}>
                            {/* Godown Selection Logic */}
                            {godowns.length === 1 && (<span className="single-text">{godowns[0].godownName}</span>)}
                            {godowns.length > 1 && (
                                <div style={{ width: "220px" }}>
                                    <Select
                                        options={godownOptions}
                                        value={godownOptions.find((opt) => opt.value === selectedGodownId) || null}
                                        onChange={(selected) => setSelectedGodownId(selected.value)}
                                        placeholder="Select Location"
                                        isSearchable
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <hr/>

                    {/* 📸 CAMERA BUTTON / PREVIEW */}
                    <div className="camera-section">

                        {/* Button to START Camera */}
                        {!cameraActive && !capturedPhoto && (
                            <button onClick={startCamera} className="action-btn primary-btn">
                                📸 Open Camera
                            </button>
                        )}
                        
                        {/* Camera Live Feed */}
                        {cameraActive && (
                            <div className='capture-container'>
                                <div className="camera-preview">
                                    <video ref={videoRef} autoPlay playsInline muted></video>
                                </div>
                                <button onClick={capturePhoto} className="capture-btn">Capture Photo</button>
                            </div>
                        )}

                        {/* Captured Photo Preview */}
                        {capturedPhoto && (
                            <div className="photo-preview">
                                <img src={capturedPhoto} alt="Your selfie" />
                                <button onClick={retakePhoto} className="action-btn retake-btn">🔄 Retake Photo</button>
                            </div>
                        )}

                        {/* Hidden canvas for capturing the photo */}
                        <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                    </div>

                    <hr/>

                    {/* 📍 LOCATION BUTTON / STATUS */}
                    <div className="location-section" style={{ marginTop: '20px', textAlign: 'center' }}>
                        {!location.latitude && (
                            <button onClick={fetchLocation} className="action-btn secondary-btn">
                                📍 Get Location
                            </button>
                        )}
                        
                        {location.latitude && (
                            <div className={`location-status ${messageType === 'success' ? 'success' : ''}`}>
                                Current Location Captured Coordinate : {location.latitude}, {location.longitude}
                            </div>
                        )}
                    </div>
                    

                    {/* SUBMIT BUTTON (Only shown after photo and location are ready) */}
                    {capturedPhoto && location.latitude && !cameraActive && (
                        <button onClick={handleSubmit} disabled={isSubmitting} className="submit-btn full-width" style={{ marginTop: '30px' }}>
                            {isSubmitting ? 'Submitting...' : 'Submit Attendance'}
                        </button>
                    )}

                    {/* MESSAGE */}
                    {submitMessage && (
                        <div className={`message ${messageType}`} style={{ marginTop: '20px' }}>
                            <strong>
                                {messageType === 'success' && 'Success'}
                                {messageType === 'duplicate' && 'Already Marked'}
                                {messageType === 'error' && 'Error'}
                            </strong>: {submitMessage}
                        </div>
                    )}

                </div>
            </div>

            {/* POPUPS (Face Error, Location Warning, Success) (UNMODIFIED) */}
            {/* ... [Face Error Popup] ... */}
            {faceErrorPopup && (
                <div className="popup-overlay">
                    <div className="popup-box">
                        <h3 className="popup-title">❌ Face Not Detected</h3>
                        <p>Please retake your photo with your full face visible.</p>
                        <button
                            className="popup-btn"
                            onClick={() => {
                                setFaceErrorPopup(false);
                                startCamera();
                            }}
                        >Retake Photo</button>
                    </div>
                </div>
            )}
            
            {/* ... [Location Warning Popup] ... */}
            {locationPopup && (
                <div className="popup-overlay">
                    <div className="popup-box">
                        <h3 className="popup-title">⚠ You Are Not at the Selected Location</h3>
                        <p>You are <b>{distanceFromGodown.toFixed(2)} km</b> away from: <b>{selectedGodownData?.godownName}</b></p>
                        <p>Do you want to proceed?</p>
                        <div className="popup-btn-row">
                            <button
                                className="popup-btn cancel"
                                onClick={() => {
                                    setLocationPopup(false);
                                    setCapturedPhoto(null);
                                    startCamera();
                                }}
                            >Cancel</button>
                            <button
                                className="popup-btn proceed"
                                onClick={() => {
                                    setLocationPopup(false);
                                    submitAttendance();
                                }}
                            >Proceed</button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* ... [Success/Duplicate Popup] ... */}
            {successPopup && (
                <div className="popup-overlay">
                    <div className="popup-box">
                        <h3 className="popup-title">✅ Attendance Captured Successfully!</h3>
                        <p>Click OK to go to your dashboard.</p>
                        <button
                            className="popup-btn proceed"
                            onClick={() => navigate('/dashboard')}
                        >OK</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CameraAndLocation;